"use client";

import { useCallback, useMemo, useState } from "react";
import { useQueryWithStore } from "@/store/store.hooks";
import { selectSupplierQuery } from "@/store/modules/suppliers/suppliers.selectors";
import { loadSuppliersQuery } from "@/store/modules/suppliers/suppliers.helpers";
import { suppliersDefaultQueryConfig } from "@/store/modules/suppliers/suppliers-default-query";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import type { iSupplier } from "@sdk/suppliers/supplier-model";
import { SupplierCard } from "./supplier-card";
import { AddSupplierCard } from "./add-supplier-card";
import { AddEditSupplierModal } from "./add-edit-supplier-modal";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, Sparkles, Truck } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { WorkspaceSectionHeader } from "../../workspace-section-header";
import { QuickAddSelectionModal } from "../shared/quick-add-selection-modal";
import {
  buildSupplierQuickAddOptions,
  buildSupplierQuickAddPayload,
  supplierNameForQuickAddOptionId,
  useWorkspaceQuickAddContext,
} from "../shared/workspace-quick-add.helpers";

type ManageSectionLayout = "settings" | "wizard";

export function ManageSuppliersSection({
  layout = "settings",
}: {
  layout?: ManageSectionLayout;
}) {
  const isWizard = layout === "wizard";
  const containerClassName = isWizard ? "space-y-4" : "space-y-6 px-4 pb-6";
  const [supplierToEdit, setSupplierToEdit] = useState<iSupplier | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [supplierToArchive, setSupplierToArchive] = useState<iSupplier | null>(
    null
  );
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const quickAddContext = useWorkspaceQuickAddContext();

  const addEditModalOpen = isAddModalOpen || supplierToEdit !== null;
  const closeAddEditModal = useCallback(() => {
    setIsAddModalOpen(false);
    setSupplierToEdit(null);
  }, []);

  const { doAction: archiveSupplier, isProcessing: isArchiveProcessing } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (id: string) =>
          SDK.suppliers.editById(id, { archived: true }),
        successMessage: "Supplier has been archived",
        failureMessage: "Could not archive supplier",
      }),
      []
    );

  const {
    state: suppliersQueryState,
    isLoading: isLoadingSuppliers,
    hasError: hasSuppliersError,
    retry: retrySuppliers,
  } = useQueryWithStore(
    selectSupplierQuery("all"),
    loadSuppliersQuery(suppliersDefaultQueryConfig, "all"),
    []
  );

  const allSuppliers = (suppliersQueryState?.list ?? []) as iSupplier[];
  const suppliers = useMemo(
    () => allSuppliers.filter((supplier) => supplier.archived !== true),
    [allSuppliers]
  );
  const archivedSuppliers = useMemo(
    () => allSuppliers.filter((supplier) => supplier.archived === true),
    [allSuppliers]
  );

  const refreshAll = useCallback(() => {
    retrySuppliers();
  }, [retrySuppliers]);

  const { doAction: createSupplier, isProcessing: isQuickAddProcessing } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (details: Partial<iSupplier>) =>
          SDK.suppliers.create(details),
        throwError: true,
      }),
      []
    );

  const quickAddOptions = useMemo(
    () => buildSupplierQuickAddOptions(suppliers, quickAddContext.currency),
    [quickAddContext.currency, suppliers]
  );

  const handleQuickAddSuppliers = useCallback(
    async (selectedIds: string[]) => {
      let createdCount = 0;
      for (const optionId of selectedIds) {
        const name = supplierNameForQuickAddOptionId(optionId);
        if (!name) continue;
        await createSupplier(
          buildSupplierQuickAddPayload(name, quickAddContext.currency)
        );
        createdCount += 1;
      }
      refreshAll();
      toast.success(
        createdCount === 1
          ? "1 supplier added"
          : `${createdCount} suppliers added`
      );
    },
    [createSupplier, quickAddContext.currency, refreshAll]
  );

  const handleConfirmArchive = useCallback(async () => {
    if (!supplierToArchive?.id) return;
    const result = await archiveSupplier(supplierToArchive.id);
    if (result !== undefined) {
      refreshAll();
      setSupplierToArchive(null);
    }
  }, [supplierToArchive, archiveSupplier, refreshAll]);

  const handleEditSupplier = useCallback((supplier: iSupplier) => {
    setIsAddModalOpen(false);
    setSupplierToEdit(supplier);
  }, []);

  if (isLoadingSuppliers) {
    return (
      <div className={containerClassName}>
        {!isWizard ? (
          <WorkspaceSectionHeader
            icon={Truck}
            title="Suppliers"
            description="Add and manage travel suppliers. Suppliers can be linked to products and used across quotes and bookings."
          />
        ) : null}
        <div className="space-y-4">
          <Skeleton className="h-4 w-48" />
          <div className="flex flex-wrap gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[88px] w-[300px] rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (hasSuppliersError) {
    return (
      <div className={containerClassName}>
        {!isWizard ? (
          <WorkspaceSectionHeader
            icon={Truck}
            title="Suppliers"
            description="Add and manage travel suppliers. Suppliers can be linked to products and used across quotes and bookings."
          />
        ) : null}
        <p className="text-muted-foreground text-sm">
          Failed to load suppliers. Please try again.
        </p>
        <Button variant="outline" onClick={() => refreshAll()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <AddEditSupplierModal
        open={addEditModalOpen}
        onOpenChange={(open) => !open && closeAddEditModal()}
        mode={supplierToEdit ? "edit" : "create"}
        supplier={supplierToEdit ?? undefined}
        retry={refreshAll}
      />
      <QuickAddSelectionModal
        open={isQuickAddOpen}
        onOpenChange={setIsQuickAddOpen}
        title="Quick add suppliers"
        description="Select common travel suppliers to add with your workspace currency."
        options={quickAddOptions}
        confirmLabel="Add suppliers"
        onConfirm={handleQuickAddSuppliers}
      />
      <AlertDialog
        open={!!supplierToArchive}
        onOpenChange={(open) => !open && setSupplierToArchive(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive supplier?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{supplierToArchive?.name}&quot; will be hidden from new
              product selections. Existing products will keep this supplier.
              You can restore it from the archived section.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmArchive();
              }}
              disabled={isArchiveProcessing}
            >
              {isArchiveProcessing ? "Archiving…" : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {!isWizard ? (
        <WorkspaceSectionHeader
          icon={Truck}
          title="Suppliers"
          description="Add and manage travel suppliers. Suppliers can be linked to products and used across quotes and bookings."
        />
      ) : null}

      <div className="space-y-4">
        {!isWizard ? (
          <div className="flex items-center gap-2">
            <Truck className="h-4 w-4 text-muted-foreground" />
            <h4 className="font-medium">Active suppliers</h4>
          </div>
        ) : null}
        <div className={cn("flex flex-row flex-wrap gap-4", !isWizard && "mt-4")}>
          {suppliers.length === 0 ? (
            <div className="flex w-full flex-col items-start gap-3">
              <p className="text-sm text-muted-foreground">
                No active suppliers yet. Add one to get started.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsQuickAddOpen(true)}
                disabled={isQuickAddProcessing}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Quick add suppliers
              </Button>
            </div>
          ) : null}
          {suppliers.map((supplier) => (
            <SupplierCard
              key={supplier.id}
              supplier={supplier}
              onEdit={() => handleEditSupplier(supplier)}
              onArchive={() => setSupplierToArchive(supplier)}
            />
          ))}
          <AddSupplierCard
            label="Add supplier"
            onClick={() => {
              setSupplierToEdit(null);
              setIsAddModalOpen(true);
            }}
          />
        </div>
      </div>

      {archivedSuppliers.length > 0 && (
        <Collapsible open={archivedOpen} onOpenChange={setArchivedOpen}>
          <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left text-sm font-medium hover:bg-muted/50">
            <span>Archived suppliers ({archivedSuppliers.length})</span>
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 transition-transform",
                archivedOpen && "rotate-180"
              )}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-4">
            <div className="flex flex-row flex-wrap gap-4">
              {archivedSuppliers.map((supplier) => (
                <SupplierCard
                  key={supplier.id}
                  supplier={supplier}
                  onEdit={() => handleEditSupplier(supplier)}
                />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
